import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCreateNode,
  useDeleteNode,
  useHotkeyTree,
  useReorderChildren,
  useUpdateNode,
  type HotkeyNode,
} from '@/hooks/use-catalog-hotkeys';
import { ChildList } from './tree-view';
import { NodeEditForm, type FormSubmit } from './node-edit-form';
import { PreviewPanel } from './preview-panel';
import { useT } from '@/i18n/use-t';
import { extractApiError } from '@/lib/api-error';

/**
 * Three-column authoring page:
 *   - Left:   tree view of the current panel (drag-to-reorder).
 *             Breadcrumb across the top + an "Up" button when
 *             drilled into a sub-group. Click a node to select.
 *   - Middle: edit form for the selected node; or new-node form when
 *             "Add node" was clicked.
 *   - Right:  preview pane rendering the currently-visible parent's
 *             children approximately as the cashier will see them.
 */
export function CatalogHotkeysPage() {
  const t = useT();
  const tree = useHotkeyTree();
  const createNode = useCreateNode();
  const updateNode = useUpdateNode();
  const deleteNode = useDeleteNode();
  const reorder = useReorderChildren();
  const qc = useQueryClient();

  // Currently-visible parent (null = root).
  const [parentId, setParentId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // 'create' mode shows the new-node form under parentId.
  const [formMode, setFormMode] = useState<'edit' | 'create'>('edit');
  // H-122 — reorder error surface (toast-style banner above the tree).
  const [reorderError, setReorderError] = useState<string | null>(null);

  const nodes: HotkeyNode[] = tree.data?.nodes ?? [];
  const byId = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);
  const visibleChildren = useMemo(
    () =>
      nodes
        .filter((n) => n.parentId === parentId)
        .slice()
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [nodes, parentId],
  );
  const selected: HotkeyNode | null = selectedId !== null ? (byId.get(selectedId) ?? null) : null;

  const breadcrumb: HotkeyNode[] = useMemo(() => {
    const path: HotkeyNode[] = [];
    let cursor: string | null = parentId;
    while (cursor !== null) {
      const node = byId.get(cursor);
      if (node === undefined) break;
      path.unshift(node);
      cursor = node.parentId;
    }
    return path;
  }, [parentId, byId]);

  async function handleFormSubmit(next: FormSubmit): Promise<void> {
    if (next.mode === 'create') {
      const created = await createNode.mutateAsync(next.input);
      setSelectedId(created.id);
      setFormMode('edit');
    } else {
      const updated = await updateNode.mutateAsync({ id: next.id, patch: next.patch });
      setSelectedId(updated.id);
    }
  }

  // RFL-206: add the chosen catalog items as child item-nodes of a group.
  // Appended after the group's current children (sortOrder past the max),
  // sequentially so each create keeps the tree's sortOrder/cap/audit
  // invariants. A failure (e.g. node cap reached) stops the run and
  // surfaces in the picker.
  async function handleAddItems(
    groupId: string,
    items: { id: string; name: string }[],
  ): Promise<void> {
    const siblings = nodes.filter((n) => n.parentId === groupId);
    let nextSort = siblings.length === 0 ? 0 : Math.max(...siblings.map((s) => s.sortOrder)) + 1;
    for (const it of items) {
      await createNode.mutateAsync({
        parentId: groupId,
        sortOrder: nextSort,
        label: it.name,
        kind: 'item',
        payload: it.id,
        iconName: null,
        categoryId: null,
      });
      nextSort += 1;
    }
  }

  async function handleDelete(): Promise<void> {
    if (selected === null) return;
    const wasParent = selected.parentId;
    await deleteNode.mutateAsync(selected.id);
    setSelectedId(null);
    // Drill out if the visible parent was the deleted node.
    if (parentId === selected.id) setParentId(wasParent);
  }

  async function handleReorder(p: string | null, orderedChildIds: string[]): Promise<void> {
    setReorderError(null);
    try {
      await reorder.mutateAsync({ parentId: p, orderedChildIds });
    } catch (err) {
      // H-122 — surface server-side reorder failures (e.g. parallel
      // admin changed the child-set first → 400 id-set mismatch).
      // The tree view reverts its local optimism on the thrown
      // rejection; the page also invalidates the tree query so the
      // banner's "tree refreshed" copy is true (H-138). RL-790: Back
      // Office's own message (field + reason), not axios's bare status line.
      const msg =
        err instanceof Error
          ? extractApiError(err)
          : t('hotkeys.tree.reorderFailed', 'Reorder failed');
      setReorderError(
        `${msg}. ${t('hotkeys.tree.reorderRetry', 'Tree refreshed — try the reorder again.')}`,
      );
      void qc.invalidateQueries({ queryKey: ['catalog-hotkeys', 'tree'] });
      throw err;
    }
  }

  function handleAddChild(p: string | null): void {
    setSelectedId(null);
    setParentId(p);
    setFormMode('create');
  }

  function handleSelect(id: string): void {
    const node = byId.get(id);
    if (node === undefined) return;
    setSelectedId(id);
    setFormMode('edit');
    // Drill in on double-tap is left for a polish iteration; for now,
    // group nodes have a "Drill into" affordance below the form.
  }

  return (
    <div className="grid h-[calc(100vh-120px)] grid-cols-[320px_1fr_360px] gap-4">
      {/* Left — tree view */}
      <section className="flex min-h-0 flex-col rounded-lg border bg-card">
        <header className="flex items-center justify-between border-b px-3 py-2">
          <h2 className="text-sm font-medium">{t('hotkeys.tree.title', 'Hotkey tree')}</h2>
          {tree.data !== undefined && (
            <span className="text-xs text-muted-foreground">v{tree.data.version}</span>
          )}
        </header>
        <div className="flex items-center gap-1 border-b px-3 py-1.5 text-xs">
          <button
            type="button"
            className="rounded px-1.5 py-0.5 text-muted-foreground hover:bg-muted/40"
            onClick={() => setParentId(null)}
          >
            {t('hotkeys.tree.root', 'Root')}
          </button>
          {breadcrumb.map((n) => (
            <span key={n.id} className="flex items-center gap-1">
              <span className="text-muted-foreground">/</span>
              <button
                type="button"
                className="rounded px-1.5 py-0.5 hover:bg-muted/40"
                onClick={() => setParentId(n.id)}
              >
                {n.label}
              </button>
            </span>
          ))}
        </div>
        {reorderError !== null && (
          <p className="border-b border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {reorderError}
          </p>
        )}
        <div className="flex-1 overflow-auto">
          {tree.isLoading ? (
            <p className="px-3 py-4 text-xs text-muted-foreground">
              {t('common.loading', 'Loading…')}
            </p>
          ) : tree.isError ? (
            <p className="px-3 py-4 text-xs text-destructive">
              {t('hotkeys.tree.loadFailed', 'Failed to load tree')}
            </p>
          ) : (
            <ChildList
              parentId={parentId}
              items={visibleChildren}
              selectedId={selectedId}
              onSelect={handleSelect}
              onReorder={handleReorder}
              onAddChild={handleAddChild}
            />
          )}
        </div>
        {selected !== null && selected.kind === 'group' && (
          <div className="border-t px-3 py-2 text-xs">
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() => setParentId(selected.id)}
            >
              {t('hotkeys.tree.drillInto', 'Drill into "{label}" →').replace(
                '{label}',
                selected.label,
              )}
            </button>
          </div>
        )}
      </section>

      {/* Middle — edit / create form */}
      <section className="rounded-lg border bg-card p-4">
        <NodeEditForm
          mode={formMode}
          selected={formMode === 'edit' ? selected : null}
          parentId={parentId}
          defaultSortOrder={visibleChildren.length}
          onSubmit={handleFormSubmit}
          onDelete={formMode === 'edit' ? handleDelete : undefined}
          onAddItems={handleAddItems}
        />
      </section>

      {/* Right — preview */}
      <section className="min-h-0">
        <PreviewPanel
          parentId={parentId}
          parentLabel={breadcrumb.at(-1)?.label ?? 'root'}
          items={visibleChildren}
        />
      </section>
    </div>
  );
}
